import React, { useEffect } from 'react';
import { useIdleTimeout } from '../../hooks/useIdleTimeout';
import { IdleTimeoutWarning } from './IdleTimeoutWarning';
import useAuth from '../../context/AuthProvider';
import { AppRoutes } from '../../helper/appRoutes';
import { IDLE_TIMEOUT_CONFIG } from '../../config/idleTimeout.config';

interface IdleTimeoutProviderProps {
  children: React.ReactNode;
  /** Idle timeout duration in milliseconds (default: 30 minutes from config) */
  timeoutMs?: number;
  /** Warning time before timeout in milliseconds (default: 2 minutes from config) */
  warningMs?: number;
}

/**
 * Provider component that manages idle timeout for authenticated users
 * Tracks activity and shows warning before logging out
 */
export const IdleTimeoutProvider: React.FC<IdleTimeoutProviderProps> = ({
  children,
  timeoutMs = IDLE_TIMEOUT_CONFIG.TIMEOUT_MS,
  warningMs = IDLE_TIMEOUT_CONFIG.WARNING_MS,
}) => {
  const { user, logout } = useAuth();

  const handleTimeout = () => {
    // Clear all session data
    logout();
    // Note: logout() already navigates to login, but we ensure it here
    window.location.href = AppRoutes.login;
  };

  const handleWarning = (timeRemaining: number) => {
    // Optional: Log warning event or show additional notifications
    console.log(`Idle timeout warning: ${Math.ceil(timeRemaining / 1000)} seconds remaining`);
  };

  const {
    isWarning,
    timeRemaining,
    extendSession,
    pauseTimer,
    resumeTimer,
  } = useIdleTimeout({
    timeoutMs,
    warningMs,
    onTimeout: handleTimeout,
    onWarning: handleWarning,
    enabled: !!user, // Only enable when user is logged in
  });

  // Expose API call tracker function globally so apiClient can use it
  useEffect(() => {
    if (user) {
      // Store the reset function so apiClient can access it
      // API calls should always reset the timer, even during warning (force reset)
      (window as any).__resetIdleTimer = () => {
        extendSession(true); // Force reset even during warning
      };
    } else {
      delete (window as any).__resetIdleTimer;
    }

    return () => {
      delete (window as any).__resetIdleTimer;
    };
  }, [user, extendSession]);

  // Pause timer when warning dialog is shown (to prevent hook's timer from firing)
  // The warning component has its own countdown that will call onTimeout
  useEffect(() => {
    if (isWarning) {
      pauseTimer();
    }
  }, [isWarning, pauseTimer]);

  const handleExtendSession = () => {
    console.log('Extending session - button clicked');
    // Extend session with force reset (this will also unpause)
    extendSession(true);
  };

  return (
    <>
      {children}
      {user && (
        <IdleTimeoutWarning
          open={isWarning}
          timeRemaining={timeRemaining}
          onExtendSession={handleExtendSession}
          onTimeout={handleTimeout}
        />
      )}
    </>
  );
};
